import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n, type MessageKey } from 'shared/i18n';
import { ApiError } from 'shared/api/client';

export type ReportFlow = 'export' | 'validation';

const CODE_SUFFIX: Record<string, string> = {
  TAXONOMY_CONFIG_REQUIRED: 'taxonomy_config_required',
  EXTENSION_SCHEMA_NOT_CONFIGURED: 'extension_schema_not_configured',
  NO_TAGS: 'no_tags',
  MISSING_UNIT_FOR_NUMERIC_FACT: 'missing_unit_for_numeric_fact',
  TAXONOMY_FAMILY_UNSUPPORTED: 'taxonomy_family_unsupported',
  NOTES_PARENT_NOT_FOUND: 'notes_parent_not_found',
  XMLNS_PREFIX_COLLISION: 'xmlns_prefix_collision',
  VALIDATOR_WARMING: 'warming',
};

export function reportErrorKey(flow: ReportFlow, code: string | undefined): MessageKey {
  const suffix = code ? CODE_SUFFIX[code] : undefined;
  return (suffix ? `tag:${flow}.error.${suffix}` : `tag:${flow}.error.generic`) as MessageKey;
}

export function extractResponseCode(err: unknown): string | undefined {
  if (err instanceof ApiError) return err.code;

  const e = err as
    | { response?: { data?: { code?: unknown } }; request?: { data?: { code?: unknown }; responseText?: unknown; response?: unknown } }
    | null
    | undefined;
  const fromRejection = e?.request?.data?.code;
  if (typeof fromRejection === 'string') return fromRejection;
  const direct = e?.response?.data?.code;
  if (typeof direct === 'string') return direct;

  const xhr = e?.request;
  if (!xhr) return undefined;

  let raw: string | undefined;
  try {
    if (typeof xhr.responseText === 'string') raw = xhr.responseText;
  } catch {
    // responseText throws when responseType is 'blob'/'arraybuffer'
  }
  if (!raw && typeof xhr.response === 'string') raw = xhr.response;
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as { code?: unknown };
    return typeof parsed?.code === 'string' ? parsed.code : undefined;
  } catch {
    return undefined;
  }
}

export function dispatchReportError(flow: ReportFlow, err: unknown): void {
  const code = extractResponseCode(err);
  const status = (err as { status?: unknown } | null | undefined)?.status;
  console.error(`[${flow}] report error`, { status, code, err });
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: i18n(reportErrorKey(flow, code)), removeTime: 6000 });
}

export function dispatchReportErrorKey(flow: ReportFlow): void {
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: i18n(`tag:${flow}.error.generic`), removeTime: 6000 });
}
